import { useEffect, useState } from 'react';
import type { FunctionComponent, ReactNode, SVGProps } from 'react';
import {
  SearchIcon,
  CaretIcon,
  CheckmarkIcon,
  HierarchyNodesIcon,
  SubInfoIcon,
  TreeCommitteeIcon,
  TreeStateIcon,
  TreeAcIcon,
} from '../../../assets/icons';

type IconType = FunctionComponent<SVGProps<SVGSVGElement>>;

/* ------------------------------------------------------------------ *
 * Sample data — full A→Z hierarchy:
 *   National Central Committee → State → Constituency (AC)
 *     → Urban  → Municipality → Ward → Booths
 *     → Rural  → Panchayat Samiti → Gram Panchayat → Village → Booths
 * ------------------------------------------------------------------ */

interface ConstituencyNode {
  id: string;
  label: string;
  icon?: IconType;
  level?: string; // badge, e.g. "LEVEL 1"
  sub?: string; // caption under the label
  groupLabel?: string; // label rendered above this node's children
  acRoot?: boolean; // marks an Assembly Constituency node
  children?: ConstituencyNode[];
}

/** A selected Assembly Constituency reported to the wizard (for step 4's Level 1). */
export interface AcOption {
  id: string;
  label: string;
}

/**
 * A pruned, read-only view of the hierarchy reported to the wizard for step 3.
 * Contains every selected node plus all of its ancestors, so the full path
 * (National Central Committee → State → AC → Urban → Municipality → …) is shown.
 */
export interface HierarchyView {
  id: string;
  label: string;
  icon?: IconType;
  level?: string;
  sub?: string;
  selected: boolean;
  children: HierarchyView[];
}

const BOOTH_PLACES = [
  'Govt. Sr. Sec. School',
  'Community Hall',
  'Primary Health Centre',
  'Municipal Office',
  'Panchayat Bhawan',
  'Anganwadi Centre',
];
const BOOTH_VOTERS = ['1,240', '980', '1,510', '760', '1,120', '890'];

/** A list of polling-booth leaf nodes. */
function booths(prefix: string, count: number): ConstituencyNode[] {
  return Array.from({ length: count }, (_, i) => ({
    id: `${prefix}-b${i + 1}`,
    label: `Booth #${101 + i} — ${BOOTH_PLACES[i % BOOTH_PLACES.length]}`,
    sub: `${BOOTH_VOTERS[i % BOOTH_VOTERS.length]} voters`,
  }));
}

/** Urban + Rural sub-hierarchies for one Assembly Constituency. */
function acSubtree(acId: string): ConstituencyNode[] {
  return [
    {
      id: `${acId}-urban`,
      label: 'Urban',
      groupLabel: 'Local body',
      children: [
        {
          id: `${acId}-municipality`,
          label: 'Municipality',
          children: [
            {
              id: `${acId}-uward`,
              label: 'Ward 4',
              groupLabel: 'Polling booths',
              children: booths(`${acId}-u`, 3),
            },
          ],
        },
      ],
    },
    {
      id: `${acId}-rural`,
      label: 'Rural',
      groupLabel: 'Local body',
      children: [
        {
          id: `${acId}-panchayat`,
          label: 'Panchayat Samiti',
          children: [
            {
              id: `${acId}-gram`,
              label: 'Gram Panchayat',
              children: [
                {
                  id: `${acId}-village`,
                  label: 'Village',
                  groupLabel: 'Polling booths',
                  children: booths(`${acId}-r`, 3),
                },
              ],
            },
          ],
        },
      ],
    },
  ];
}

/** An Assembly Constituency node with its full Urban/Rural sub-tree. */
const ac = (id: string, label: string, sub: string): ConstituencyNode => ({
  id,
  label,
  icon: TreeAcIcon,
  sub,
  groupLabel: 'Settlement type',
  acRoot: true,
  children: acSubtree(id),
});

const CONSTITUENCY_TREE: ConstituencyNode[] = [
  {
    id: 'ncc',
    label: 'National Central Committee',
    icon: TreeCommitteeIcon,
    level: 'LEVEL 1',
    children: [
      {
        id: 'rajasthan',
        label: 'Rajasthan',
        icon: TreeStateIcon,
        level: 'LEVEL 2',
        groupLabel: 'Parliament consituency',
        children: [
          ac('alwar', 'Alwar AC', 'Rural and Urban'),
          ac('ajmer', 'Ajmer AC', 'Urban'),
          ac('jaipur', 'Jaipur AC', 'Urban'),
        ],
      },
      {
        id: 'maharashtra',
        label: 'Maharashtra State Unit',
        icon: TreeStateIcon,
        level: 'LEVEL 2',
        groupLabel: 'Parliament consituency',
        children: [
          ac('mumbai', 'Mumbai AC', 'Urban'),
          ac('pune', 'Pune AC', 'Rural and Urban'),
          ac('nagpur', 'Nagpur AC', 'Urban'),
        ],
      },
      {
        id: 'karnataka',
        label: 'Karnataka State Unit',
        icon: TreeStateIcon,
        level: 'LEVEL 2',
        groupLabel: 'Parliament consituency',
        children: [
          ac('bengaluru', 'Bengaluru AC', 'Urban'),
          ac('mysuru', 'Mysuru AC', 'Rural and Urban'),
        ],
      },
    ],
  },
];

/* ------------------------------------------------------------------ *
 * Branch metadata — Urban vs Rural are mutually exclusive within an AC.
 * ------------------------------------------------------------------ */

type Branch = 'urban' | 'rural';
interface BranchInfo {
  acId: string | null;
  branch: Branch | null;
}

/** Tag every node with the AC + settlement branch it belongs to (null above AC level). */
function buildBranchMeta(
  nodes: ConstituencyNode[],
  acId: string | null,
  branch: Branch | null,
  meta: Record<string, BranchInfo>,
): Record<string, BranchInfo> {
  for (const n of nodes) {
    let nAcId = acId;
    let nBranch = branch;
    if (n.id.endsWith('-urban')) {
      nBranch = 'urban';
      nAcId = n.id.slice(0, -'-urban'.length);
    } else if (n.id.endsWith('-rural')) {
      nBranch = 'rural';
      nAcId = n.id.slice(0, -'-rural'.length);
    }
    meta[n.id] = { acId: nBranch ? nAcId : null, branch: nBranch };
    if (n.children) buildBranchMeta(n.children, nAcId, nBranch, meta);
  }
  return meta;
}

const BRANCH_META = buildBranchMeta(CONSTITUENCY_TREE, null, null, {});

/** Map every node to the Assembly Constituency it belongs to (incl. the AC node itself). */
const AC_LABELS: Record<string, string> = {};
function buildAcMap(
  nodes: ConstituencyNode[],
  currentAc: string | null,
  map: Record<string, string>,
): Record<string, string> {
  for (const n of nodes) {
    let acId = currentAc;
    if (n.acRoot) {
      acId = n.id;
      AC_LABELS[n.id] = n.label;
    }
    if (acId) map[n.id] = acId;
    if (n.children) buildAcMap(n.children, acId, map);
  }
  return map;
}
const NODE_AC = buildAcMap(CONSTITUENCY_TREE, null, {});

/* ------------------------------------------------------------------ *
 * Shared bits
 * ------------------------------------------------------------------ */

function Checkbox({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={disabled ? undefined : onChange}
      className={[
        'flex size-[18px] shrink-0 items-center justify-center rounded transition-colors',
        disabled
          ? 'cursor-not-allowed border border-[#e2e8f0] bg-[#f1f5f9]'
          : checked
            ? 'bg-brand text-white'
            : 'border border-[#cbd5e1] bg-white',
      ].join(' ')}
    >
      {checked && !disabled && <CheckmarkIcon className="size-4" />}
    </button>
  );
}

function Caret({
  hasChildren,
  expanded,
  onClick,
}: {
  hasChildren: boolean;
  expanded?: boolean;
  onClick?: () => void;
}) {
  if (!hasChildren) return <span className="size-4 shrink-0" />;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex size-4 shrink-0 items-center justify-center text-[#94a3b8]"
      aria-label={expanded ? 'Collapse' : 'Expand'}
    >
      <CaretIcon className={['size-4 transition-transform', expanded ? '' : '-rotate-90'].join(' ')} />
    </button>
  );
}

function LevelBadge({ children }: { children: ReactNode }) {
  return (
    <span className="rounded bg-[#f1f5f9] px-2 py-1 text-[10px] font-bold tracking-[0.25px] text-[#64748b]">
      {children}
    </span>
  );
}

function SelectedPill() {
  return (
    <span className="flex items-center gap-1.5">
      <span className="size-2 rounded-full bg-[#10b981]" />
      <span className="text-[10px] font-bold tracking-[0.5px] text-[#059669]">SELECTED</span>
    </span>
  );
}

function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={[
        'flex h-[640px] flex-col overflow-hidden rounded-xl border border-line-soft bg-white shadow-topbar',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

/** Toggle helper for a Set held in state. */
function useToggleSet(initial: string[]) {
  const [set, setSet] = useState<Set<string>>(new Set(initial));
  const has = (id: string) => set.has(id);
  const toggle = (id: string) =>
    setSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return { has, toggle, set };
}

/**
 * Prune the tree for the read-only step-3 view. A node is kept when:
 *   - it is selected, or is inside a selected branch (a selected ancestor) — in
 *     which case its FULL subtree is shown, or
 *   - it is an ancestor of a selection (has a selected descendant) — shown as the
 *     path leading down to it.
 * So selecting a State shows the entire hierarchy beneath it, with the path above.
 */
function pruneTree(
  nodes: ConstituencyNode[],
  selected: Set<string>,
  underSelected = false,
): HierarchyView[] {
  const out: HierarchyView[] = [];
  for (const n of nodes) {
    const isSelected = selected.has(n.id);
    const inSelectedBranch = underSelected || isSelected;
    const children = n.children ? pruneTree(n.children, selected, inSelectedBranch) : [];
    if (inSelectedBranch || children.length > 0) {
      out.push({
        id: n.id,
        label: n.label,
        icon: n.icon,
        level: n.level,
        sub: n.sub,
        selected: isSelected,
        children,
      });
    }
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * Tree — full constituency + settlement + booth hierarchy
 * ------------------------------------------------------------------ */

const ROW = 'flex items-center gap-2.5 py-2';
const ROW_SELECTED = 'flex items-center gap-2.5 rounded-lg border border-[#bfdbfe] bg-[#eff6ff] px-3 py-2.5';

function ConstituencyTree({
  onSelectionChange,
  onBranchesChange,
  onConstituenciesChange,
}: {
  onSelectionChange?: (nodes: HierarchyView[]) => void;
  onBranchesChange?: (branches: Record<string, Branch>) => void;
  onConstituenciesChange?: (acs: AcOption[]) => void;
}) {
  const expand = useToggleSet([
    'ncc',
    'rajasthan',
    'alwar',
    'alwar-urban',
    'alwar-municipality',
    'alwar-uward',
  ]);
  const select = useToggleSet(['alwar']);

  useEffect(() => {
    onSelectionChange?.(pruneTree(CONSTITUENCY_TREE, select.set));
    if (onBranchesChange) {
      // Per-AC branch, derived from the ACTUAL selected nodes (not the pruned view).
      // Selecting only the AC node doesn't choose a branch, so that AC shows both.
      const byAc: Record<string, Branch> = {};
      select.set.forEach((selId) => {
        const m = BRANCH_META[selId];
        if (m?.branch && m.acId) byAc[m.acId] = m.branch;
      });
      onBranchesChange(byAc);
    }
    if (onConstituenciesChange) {
      // The set of Assembly Constituencies touched by the current selection.
      const acIds: string[] = [];
      select.set.forEach((selId) => {
        const acId = NODE_AC[selId];
        if (acId && !acIds.includes(acId)) acIds.push(acId);
      });
      onConstituenciesChange(acIds.map((acId) => ({ id: acId, label: AC_LABELS[acId] })));
    }
  }, [select.set, onSelectionChange, onBranchesChange, onConstituenciesChange]);

  // Which settlement branch (urban/rural) is chosen per AC — the other is disabled.
  const chosenBranch: Record<string, Branch> = {};
  select.set.forEach((selId) => {
    const m = BRANCH_META[selId];
    if (m?.branch && m.acId) chosenBranch[m.acId] = m.branch;
  });

  const renderNode = (node: ConstituencyNode) => {
    const hasChildren = Boolean(node.children?.length);
    const open = expand.has(node.id);
    const selected = select.has(node.id);
    const meta = BRANCH_META[node.id];
    const disabled = Boolean(
      meta?.branch && meta.acId && chosenBranch[meta.acId] && chosenBranch[meta.acId] !== meta.branch,
    );
    return (
      <div key={node.id}>
        <div className={[selected ? ROW_SELECTED : ROW, disabled ? 'opacity-60' : ''].join(' ')}>
          <Caret hasChildren={hasChildren} expanded={open} onClick={() => expand.toggle(node.id)} />
          <Checkbox
            checked={selected}
            disabled={disabled}
            onChange={() => select.toggle(node.id)}
          />
          {node.icon ? (
            <node.icon className="size-5 shrink-0 text-brand" />
          ) : (
            <span className="flex size-5 shrink-0 items-center justify-center">
              <span className="size-1.5 rounded-full bg-[#94a3b8]" />
            </span>
          )}
          <button
            type="button"
            onClick={() => {
              if (hasChildren) expand.toggle(node.id);
              else if (!disabled) select.toggle(node.id);
            }}
            className={[
              'text-left text-sm',
              disabled
                ? 'text-[#94a3b8]'
                : selected
                  ? 'font-semibold text-brand'
                  : 'font-medium text-ink',
            ].join(' ')}
          >
            {node.label}
          </button>
          {node.level ? (
            <span className="ml-auto">
              <LevelBadge>{node.level}</LevelBadge>
            </span>
          ) : (
            selected && <span className="ml-auto"><SelectedPill /></span>
          )}
        </div>

        {node.sub && (
          <div className="flex items-center gap-1.5 pl-8 pt-1 text-xs text-[#94a3b8]">
            <SubInfoIcon className="h-[9px] w-[10px]" />
            {node.sub}
          </div>
        )}

        {hasChildren && open && (
          <div className="ml-[19px] flex flex-col gap-1.5 border-l border-dashed border-[#cbd5e1] pl-3">
            {node.groupLabel && (
              <p className="py-1 pl-6 text-xs font-medium text-[#94a3b8]">{node.groupLabel}</p>
            )}
            {node.children!.map(renderNode)}
          </div>
        )}
      </div>
    );
  };

  return <div className="flex flex-col gap-1">{CONSTITUENCY_TREE.map(renderNode)}</div>;
}

/* ------------------------------------------------------------------ *
 * Step
 * ------------------------------------------------------------------ */

/** Step 2 — Define Operational Boundary (full constituency → booth hierarchy). */
export default function DefineBoundaryStep({
  onSelectionChange,
  onBranchesChange,
  onConstituenciesChange,
}: {
  onSelectionChange?: (nodes: HierarchyView[]) => void;
  onBranchesChange?: (branches: Record<string, Branch>) => void;
  onConstituenciesChange?: (acs: AcOption[]) => void;
}) {
  return (
    <div className="flex flex-col gap-8">
      {/* Search */}
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-[#94a3b8]" />
        <input
          type="search"
          placeholder="Search location"
          className="w-full rounded-lg border border-line-soft bg-white py-3.5 pl-[41px] pr-3.5 text-sm text-ink shadow-card placeholder:text-[#94a3b8] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
        />
      </div>

      {/* Full hierarchy selector */}
      <Card>
        <div className="flex items-center justify-between border-b border-[#f1f5f9] px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="flex size-8 items-center justify-center rounded-md bg-[#eff6ff] text-brand">
              <HierarchyNodesIcon className="size-5" />
            </span>
            <h2 className="text-base font-semibold text-ink">Select Constituency Node</h2>
          </div>
          <div className="flex items-center gap-1 text-[#94a3b8]">
            <button
              type="button"
              aria-label="Collapse all"
              className="flex size-6 items-center justify-center"
            >
              <CaretIcon className="size-4 rotate-180" />
            </button>
            <button
              type="button"
              aria-label="Expand all"
              className="flex size-6 items-center justify-center"
            >
              <CaretIcon className="size-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto px-4 py-6">
          <ConstituencyTree
            onSelectionChange={onSelectionChange}
            onBranchesChange={onBranchesChange}
            onConstituenciesChange={onConstituenciesChange}
          />
        </div>
      </Card>
    </div>
  );
}
